<script lang="ts" module>
  import { Select } from 'bits-ui';
  import { tv } from 'tailwind-variants';

  import Icon from '@/ui/base/icon/Icon.svelte';

  import DropDownLoginSelect, {
    type DropDownLoginItem,
  } from './DropDownLogin/DropDownLoginSelect.svelte';

  export interface DropDownLoginProps {
    items: DropDownLoginItem[];
    label: string;
    selected?: string;
    onItemClick?: (item: string) => void;
    onValueChange?: (value: string) => void;
    disabled?: boolean;
    ariaLabel?: string;
    errorMsg?: string;
  }
</script>

<script lang="ts">
  let {
    selected: selectedProp = $bindable(),
    errorMsg: errorMsgProp = '',
    label: labelProp,
    ...props
  }: DropDownLoginProps = $props();

  const dropDownStyles = tv({
    base: 'flex h-12 w-full items-center rounded-t-sm  border-b bg-dropdownlogin-H py-1 pl-4 pr-3',
    variants: {
      open: {
        true: 'border-c-login-open',
        false: 'border-c-login',
      },
      selected: {
        true: '!border-c-login-selected',
        false: '',
      },
      error: {
        true: '!border-c-login-error',
        false: '',
      },
    },
  });

  const textStyle = tv({
    base: 'flex text-left font-sans  font-normal text-c-base',
    variants: {
      labelPos: {
        up: 'h-auto items-start text-xs',
        down: 'h-full items-center text-md',
      },
      error: {
        true: '!text-c-error',
        false: '',
      },
    },
  });

  let selectedLabel: string | undefined = $derived(
    selectedProp ? props.items.find((item) => item.value === selectedProp)?.label : undefined,
  );
  let isOpen: boolean = $state(false);
</script>

<div class="grid w-full gap-2">
  <Select.Root
    disabled={props.disabled}
    bind:open={isOpen}
    type="single"
    onValueChange={(v) => {
      selectedProp = v;
      props.onItemClick?.(v);
      props.onValueChange?.(v);
    }}
  >
    <Select.Trigger
      class={dropDownStyles({
        open: isOpen,
        selected: selectedLabel !== undefined,
        error: errorMsgProp.length > 0,
      })}
      aria-label={props.ariaLabel ?? 'Select'}
    >
      <div class="grid h-full flex-1 gap-[2px]">
        <label
          for="UserSelect"
          class={textStyle({
            labelPos: isOpen || selectedLabel ? 'up' : 'down',
            error: errorMsgProp.length > 0,
          })}
        >
          {labelProp}
        </label>
        {#if selectedLabel}
          <p class="text-left font-sans text-md font-normal text-c-base">{selectedLabel}</p>
        {/if}
      </div>
      <Icon icon="KeyboardArrowDown" class="size-4" />
    </Select.Trigger>

    <Select.Portal>
      <Select.Content align="start" id="UserSelect">
        <Select.Viewport class="grid w-[280px] rounded-b-md bg-white shadow-card">
          {#each props.items as item (item)}
            <Select.Item value={item.value} label={item.label} disabled={item.disabled}>
              <DropDownLoginSelect selected={item.value === selectedProp} {item}
              ></DropDownLoginSelect>
            </Select.Item>
          {/each}
        </Select.Viewport>
      </Select.Content>
    </Select.Portal>
  </Select.Root>
  {#if errorMsgProp}
    <p class="px-4 font-sans text-xs font-normal text-c-error" role="alert">
      {errorMsgProp}
    </p>
  {/if}
</div>
